import { P } from '@dnb/eufemia';
import { Form, Value } from '@dnb/eufemia/extensions/forms';
import { SeverityClassification } from '@portals/shared/portal/ApiDto';
import { type JSX } from 'react';

import ServiceNowIdentifierViewer from '@/components/ServiceNowIdentifierViewer';
import { ServiceDomainResponse } from '@/openapi/schema';
import { ServiceNowInfoFormData } from '@/pages/OAuthIntegrationInfoRequesterPage/ServiceNowInfoSection/index';
import { FormEditButton } from '@/pages/profile/apps/application/ApiProviderPage/components/FormEditButton';
import { SwitchContainerModeRef } from '@/types/SwitchContainerModeTypes';

import { SERVICE_NOW_INFO_FORM_ID, SEVERITY_CLASSIFICATION_OPTIONS } from './constants';

interface ViewContainerProps {
  serviceDomains: ServiceDomainResponse[];
  isTeamAdmin: boolean;
  onEdit: () => void;
  switchContainerModeRef: SwitchContainerModeRef;
}

export default function ViewContainer({
  serviceDomains,
  isTeamAdmin,
  onEdit,
  switchContainerModeRef,
}: ViewContainerProps): JSX.Element {
  const {
    data: { serviceNowIdentifier },
  } = Form.useData<ServiceNowInfoFormData>(SERVICE_NOW_INFO_FORM_ID);

  return (
    <Form.Section.ViewContainer onEdit={onEdit}>
      <Form.MainHeading>ServiceNow Information</Form.MainHeading>

      <div>
        <P bottom="small" weight="medium">
          ServiceNow Identifier (BA/BSN)
        </P>
        <ServiceNowIdentifierViewer serviceNowIdentifier={serviceNowIdentifier} />
      </div>

      <Value.SummaryList>
        <Value.String
          label="Service Domain"
          path="/serviceNowServiceDomainId"
          placeholder="Not set"
          transformIn={(serviceDomainId) => {
            if (!serviceDomainId) return null;
            const domain = serviceDomains.find((it) => it.id === serviceDomainId);
            if (!domain) return `Unknown Service Domain`;
            return `${domain.name} - ${domain.serviceDomainId}`;
          }}
        />
        <Value.String
          label="Severity Classification"
          path="/severityClassification"
          placeholder="Not set"
          transformIn={(value) => SEVERITY_CLASSIFICATION_OPTIONS[value as SeverityClassification]}
        />
        <Value.Boolean label="Exposing Personally Identifiable Information" path="/piiExposed" placeholder="Not set" />
        <Value.Boolean label="GDPR Compliant" path="/gdprCompliant" placeholder="Not set" />
      </Value.SummaryList>

      <Form.Section.Toolbar>
        <FormEditButton
          disabled={!isTeamAdmin}
          disabledTooltip={<P>Only team admins have access to edit</P>}
          onEdit={onEdit}
          switchContainerModeRef={switchContainerModeRef}
        />
      </Form.Section.Toolbar>
    </Form.Section.ViewContainer>
  );
}
